/* Living engine — the maths behind the "alive" UI.
 *
 * Everything here is pure (no React) so it can be reused by the backdrop
 * canvas, the dashboard sky scene and the theme provider:
 *   - real sun position (elevation + azimuth) for the user's location
 *   - a continuous sky palette derived from the sun's elevation
 *   - season (hemisphere aware) and a coarse weather "mood" from WMO codes
 *   - a tiny pointer / gyroscope parallax bus shared by all 3D elements
 */

const RAD = Math.PI / 180;

export const DEFAULT_LOCATION = { lat: 55.6761, lon: 12.5683 }; // Copenhagen — same fallback as the weather widget

export const clamp = (v, min = 0, max = 1) => Math.min(max, Math.max(min, v));
export const lerp = (a, b, t) => a + (b - a) * t;

/** Solar position (NOAA low-precision algorithm, accurate to ~1°). */
export function sunPosition(date, lat, lon) {
    const d = (date.getTime() - 946728000000) / 86400000; // days since J2000.0
    const g = (357.529 + 0.98560028 * d) * RAD;
    const q = 280.459 + 0.98564736 * d;
    const L = (q + 1.915 * Math.sin(g) + 0.02 * Math.sin(2 * g)) * RAD;
    const e = (23.439 - 0.00000036 * d) * RAD;
    const ra = Math.atan2(Math.cos(e) * Math.sin(L), Math.cos(L)) / RAD;
    const decl = Math.asin(Math.sin(e) * Math.sin(L));
    const gmst = ((18.697374558 + 24.06570982441908 * d) % 24 + 24) % 24;
    const H = ((gmst * 15 + lon - ra) % 360 + 540) % 360 - 180; // hour angle, -180..180
    const phi = lat * RAD;
    const h = H * RAD;
    const elevation = Math.asin(Math.sin(phi) * Math.sin(decl) + Math.cos(phi) * Math.cos(decl) * Math.cos(h)) / RAD;
    const azimuth = (Math.atan2(-Math.sin(h), Math.tan(decl) * Math.cos(phi) - Math.sin(phi) * Math.cos(h)) / RAD + 360) % 360;
    return { elevation, azimuth, hourAngle: H };
}

/** Phase of day from the sun — rising vs. setting decides dawn/morning vs. golden/dusk. */
export function dayPhase(elevation, rising) {
    if (elevation < -10) return 'night';
    if (elevation < -2) return rising ? 'dawn' : 'dusk';
    if (elevation < 12) return rising ? 'morning' : 'golden';
    return 'day';
}

export function seasonFor(date, lat) {
    const m = date.getMonth(); // 0..11
    const north = ['winter', 'winter', 'spring', 'spring', 'spring', 'summer', 'summer', 'summer', 'autumn', 'autumn', 'autumn', 'winter'][m];
    if (lat >= 0) return north;
    return { winter: 'summer', summer: 'winter', spring: 'autumn', autumn: 'spring' }[north];
}

/** Collapse an Open-Meteo WMO weather code into a visual mood. */
export function weatherMood(code) {
    if (code == null) return 'clear';
    if (code <= 1) return 'clear';
    if (code === 2) return 'partly';
    if (code === 3) return 'cloudy';
    if (code === 45 || code === 48) return 'fog';
    if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) return 'rain';
    if ((code >= 71 && code <= 77) || code === 85 || code === 86) return 'snow';
    if (code >= 95) return 'storm';
    return 'cloudy';
}

/* Everything Open-Meteo's WMO code + measurements tell us about the weather
 * right now: precipitation type and strength, thunder, hail, fog, ice, wind.
 * Wind values are m/s.
 */
const INTENSITY = {
    51: 0.3, 53: 0.5, 55: 0.7, 56: 0.35, 57: 0.6, 61: 0.4, 63: 0.65, 65: 1, 66: 0.5, 67: 0.9,
    71: 0.35, 73: 0.65, 75: 1, 77: 0.5, 80: 0.45, 81: 0.7, 82: 1, 85: 0.5, 86: 0.9, 95: 0.8, 96: 0.9, 99: 1,
};
export function weatherDetail(code, { temp = null, wind = null, gusts = null, cloud = null } = {}) {
    const c = code ?? 0;
    const d = { code: c, precip: 'none', intensity: INTENSITY[c] || 0, thunder: false, hail: false, fog: false, icy: false, showers: false, windy: false, gale: false, cloud };
    if (c === 45 || c === 48) { d.fog = true; d.icy = c === 48; }
    else if (c >= 51 && c <= 55) d.precip = 'drizzle';
    else if (c === 56 || c === 57 || c === 66 || c === 67) { d.precip = 'freezing'; d.icy = true; }
    else if (c >= 61 && c <= 65) d.precip = 'rain';
    else if (c >= 71 && c <= 75) d.precip = 'snow';
    else if (c === 77) d.precip = 'grains';
    else if (c >= 80 && c <= 82) { d.precip = 'rain'; d.showers = true; }
    else if (c === 85 || c === 86) { d.precip = 'snow'; d.showers = true; }
    else if (c >= 95) { d.thunder = true; d.precip = 'rain'; d.hail = c >= 96; }
    // Rain that falls through near-freezing air arrives as sleet
    if ((d.precip === 'rain' || d.precip === 'drizzle') && temp != null && temp <= 1.5) d.precip = 'sleet';
    if (temp != null && temp <= 0) d.icy = true;
    const w = Math.max(wind ?? 0, (gusts ?? 0) * 0.7);
    d.windy = w >= 10.8; // Beaufort 6 — strong breeze
    d.gale = w >= 17.2; // Beaufort 8 — gale / storm
    if (d.cloud == null) d.cloud = { 0: 5, 1: 20, 2: 50, 3: 95 }[c] ?? (c >= 45 ? 90 : 50);
    return d;
}

// Sky gradient keyed on sun elevation: [elevation, top, bottom, glow]
const SKY_STOPS = [
    [-18, '#0b1026', '#1a2145', '#8a9bff'],
    [-9, '#1b2150', '#56386b', '#c38bff'],
    [-3, '#34447f', '#ef8a62', '#ffa36b'],
    [3, '#6f98d2', '#ffc28c', '#ffcf7a'],
    [12, '#86bdf5', '#fde3c2', '#ffe9a8'],
    [35, '#9bd0ff', '#e8f5ff', '#fff6d8'],
];

function hexToRgb(hex) {
    const n = parseInt(hex.slice(1), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
function mixHex(a, b, t) {
    const [r1, g1, b1] = hexToRgb(a);
    const [r2, g2, b2] = hexToRgb(b);
    const c = (x, y) => Math.round(lerp(x, y, t)).toString(16).padStart(2, '0');
    return `#${c(r1, r2)}${c(g1, g2)}${c(b1, b2)}`;
}

export function skyPalette(elevation, mood = 'clear') {
    let i = 0;
    while (i < SKY_STOPS.length - 2 && elevation > SKY_STOPS[i + 1][0]) i++;
    const [e1, t1, b1, g1] = SKY_STOPS[i];
    const [e2, t2, b2, g2] = SKY_STOPS[i + 1];
    const t = clamp((elevation - e1) / (e2 - e1));
    let top = mixHex(t1, t2, t);
    let bottom = mixHex(b1, b2, t);
    let glow = mixHex(g1, g2, t);
    // Overcast skies desaturate towards slate grey
    const grey = { cloudy: 0.45, rain: 0.55, storm: 0.7, fog: 0.5, snow: 0.35, partly: 0.15 }[mood] || 0;
    if (grey) {
        const slate = elevation < -6 ? '#1c2230' : '#9aa6b5';
        top = mixHex(top, slate, grey);
        bottom = mixHex(bottom, elevation < -6 ? '#2a3140' : '#d5dbe3', grey);
        glow = mixHex(glow, '#e8ecf2', grey * 0.6);
    }
    return { top, bottom, glow };
}

/** 0 at deep night → 1 in full daylight. */
export const daylight = (elevation) => clamp((elevation + 8) / 20);

/** Snapshot of everything the living UI reacts to. */
export function livingSnapshot({ lat, lon, weatherCode, wind, gusts, cloud, temp }, now = new Date()) {
    const sun = sunPosition(now, lat, lon);
    const later = sunPosition(new Date(now.getTime() + 20 * 60000), lat, lon);
    const rising = later.elevation > sun.elevation;
    const wx = weatherDetail(weatherCode, { temp, wind, gusts, cloud });
    let mood = weatherMood(weatherCode);
    if (wx.gale && (mood === 'clear' || mood === 'partly' || mood === 'cloudy')) mood = 'cloudy';
    if (wx.precip === 'sleet' || wx.precip === 'freezing') mood = 'rain';
    const hour = now.getHours();
    return {
        ...sun,
        rising,
        phase: dayPhase(sun.elevation, rising),
        season: seasonFor(now, lat),
        mood,
        weatherCode: weatherCode ?? null,
        wx,
        hour,
        // Clock-based nuance on top of the sun phase: early morning / evening / late night
        clock: hour < 4 ? 'late' : hour < 7 ? 'early' : hour >= 22 ? 'late' : hour >= 18 ? 'evening' : 'day',
        wind: clamp(Math.max(wind ?? 2.5, (gusts ?? 0) * 0.7) / 11), // m/s → 0..1 (gusts count)
        temp: temp ?? null,
        daylight: daylight(sun.elevation),
        sky: skyPalette(sun.elevation, mood),
        isNight: sun.elevation < -4,
        // Screen-space sun position: hour angle works for both hemispheres
        sunX: clamp(50 + (sun.hourAngle / 90) * 40, 4, 96),
        sunY: clamp(46 - sun.elevation * 0.75, 6, 96),
    };
}

/* ---------- Motion preference ---------- */

export function prefersReducedMotion() {
    try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
}

/** Keep the mobile default light; an explicit Profile preference wins. */
export function recommendedMotion() {
    if (prefersReducedMotion()) return 'off';
    try {
        if (window.matchMedia('(pointer: coarse)').matches || (navigator.deviceMemory && navigator.deviceMemory <= 4)) return 'calm';
    } catch { /* unsupported capability */ }
    return 'full';
}

export function motionLevel() {
    return document.documentElement.dataset.motion || 'full';
}

/* ---------- Haptics ---------- */

export function haptic(pattern = 8) {
    if (motionLevel() === 'off') return;
    try { navigator.vibrate?.(pattern); } catch { /* unsupported */ }
}

/* ---------- Scroll state ----------
 * While the page scrolls, the costly decoration (gyro parallax, canvas
 * particles, foil and ken-burns animations) holds still so the scroll itself
 * stays smooth. html.is-scrolling lets plain CSS do the same.
 */
let scrolling = false;
let scrollTimer = null;
let scrollWatched = false;

export const isScrolling = () => scrolling;

export function watchScroll() {
    if (scrollWatched || typeof document === 'undefined') return;
    scrollWatched = true;
    const root = document.documentElement;
    const onScroll = () => {
        if (!scrolling) { scrolling = true; root.classList.add('is-scrolling'); }
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => { scrolling = false; root.classList.remove('is-scrolling'); kick(); }, 160);
    };
    // Capture catches scrolling inside overflow containers and modals too
    document.addEventListener('scroll', onScroll, { passive: true, capture: true });
}

/* ---------- Parallax bus (pointer + gyroscope) ----------
 * Elements register themselves and receive --gx / --gy (-1..1) directly on
 * their own style, so we never invalidate the whole document per frame.
 * Only elements on screen are updated; each gets .is-inview so CSS can keep
 * off-screen decoration idle.
 */

const parallaxEls = new Set();
const visibleEls = new Set();
let viewObserver = null;
let target = { x: 0, y: 0 };
let current = { x: 0, y: 0 };
let written = { x: NaN, y: NaN };
let rafId = null;
let started = false;

function writeParallax(el) {
    el.style.setProperty('--gx', current.x.toFixed(3));
    el.style.setProperty('--gy', current.y.toFixed(3));
}

function tick() {
    rafId = null;
    current.x = lerp(current.x, target.x, 0.1);
    current.y = lerp(current.y, target.y, 0.1);
    const settled = Math.abs(current.x - target.x) < 0.002 && Math.abs(current.y - target.y) < 0.002;
    // Hand tremor would otherwise restyle every card on every frame
    const moved = Math.abs(current.x - written.x) > 0.004 || Math.abs(current.y - written.y) > 0.004 || Number.isNaN(written.x);
    if (scrolling) return; // resumed by the scroll watcher
    if (moved || settled) {
        written = { ...current };
        visibleEls.forEach(writeParallax);
    }
    if (!settled) rafId = requestAnimationFrame(tick);
}

function kick() {
    if (rafId == null && started) rafId = requestAnimationFrame(tick);
}

function startParallax() {
    if (started || typeof window === 'undefined') return;
    started = true;
    watchScroll();
    window.addEventListener('pointermove', (e) => {
        if (e.pointerType !== 'mouse' || motionLevel() !== 'full') return;
        target = { x: (e.clientX / window.innerWidth) * 2 - 1, y: (e.clientY / window.innerHeight) * 2 - 1 };
        kick();
    }, { passive: true });
    let base = null;
    window.addEventListener('deviceorientation', (e) => {
        if (e.gamma == null || e.beta == null || motionLevel() !== 'full' || document.hidden) return;
        // Relative to the way the phone was first held, so resting angle ≠ tilt.
        if (!base) base = { g: e.gamma, b: e.beta };
        base.g = lerp(base.g, e.gamma, 0.004);
        base.b = lerp(base.b, e.beta, 0.004);
        const next = { x: clamp((e.gamma - base.g) / 25, -1, 1), y: clamp((e.beta - base.b) / 25, -1, 1) };
        if (Math.abs(next.x - target.x) < 0.01 && Math.abs(next.y - target.y) < 0.01) return;
        target = next;
        kick();
    }, { passive: true });
}

function viewport() {
    if (viewObserver !== null) return viewObserver;
    viewObserver = typeof IntersectionObserver === 'undefined' ? false : new IntersectionObserver((entries) => {
        entries.forEach(({ target: el, isIntersecting }) => {
            el.classList.toggle('is-inview', isIntersecting);
            if (isIntersecting && parallaxEls.has(el)) { visibleEls.add(el); writeParallax(el); } else visibleEls.delete(el);
        });
    }, { rootMargin: '120px 0px' });
    return viewObserver;
}

/** Calls back with true/false as the element enters / leaves the screen. */
const viewCallbacks = new WeakMap();
let cbObserver = null;
export function observeInView(el, cb, rootMargin = '200px 0px') {
    if (!el) return () => {};
    if (typeof IntersectionObserver === 'undefined') { cb(true); return () => {}; }
    if (!cbObserver) {
        cbObserver = new IntersectionObserver((entries) => {
            entries.forEach((e) => viewCallbacks.get(e.target)?.(e.isIntersecting));
        }, { rootMargin });
    }
    viewCallbacks.set(el, cb);
    cbObserver.observe(el);
    return () => { cbObserver.unobserve(el); viewCallbacks.delete(el); };
}

export function registerParallax(el) {
    if (!el) return () => {};
    startParallax();
    parallaxEls.add(el);
    writeParallax(el);
    const io = viewport();
    if (io) io.observe(el);
    else { visibleEls.add(el); el.classList.add('is-inview'); }
    return () => {
        parallaxEls.delete(el);
        visibleEls.delete(el);
        if (io) io.unobserve(el);
    };
}

/** Deterministic pseudo-random from a string — keeps each picture's motion unique but stable. */
export function seeded(str = '') {
    let h = 2166136261;
    for (let i = 0; i < str.length; i++) {
        h ^= str.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return () => {
        h = Math.imul(h ^ (h >>> 15), 2246822507);
        h = Math.imul(h ^ (h >>> 13), 3266489909);
        return ((h ^= h >>> 16) >>> 0) / 4294967296;
    };
}
